/* components/ProductsFilters
- Used within collection and search templates

Example
<collection-filters>
    [[collection filters content]]
</collection-filters>
*/

import { Component, Ref } from '@eastsideco/salvo-ts';
import { ThemeComponent } from '@/theme/ThemeComponent';

@Component
export class CollectionFilters extends ThemeComponent {
    @Ref()
    details!: HTMLDListElement[];

    init() {
        this.handleDetailsOpen(this.details);
        this.handleApplyButtons();
        this.handleClickOutside();
        this.handlePlantTypeFilters();
    }

    private handleDetailsOpen(details: HTMLDListElement[]) {
        for (const detail of details) {
            detail.addEventListener("click", () => {
                for (const sibling of details) {
                    if (detail !== sibling) {
                        sibling.removeAttribute("open");
                    }
                }
            });

            const checkBoxes = detail.querySelectorAll('input[type="checkbox"]') as NodeListOf<HTMLInputElement>;
            const selectHeader = detail.querySelector('[data-ref="headerSelected"]');

            // Calculate initial count correctly
            const getCheckedCount = () => {
                let count = 0;
                for (const checkbox of checkBoxes) {
                    if (checkbox.checked) {
                        count += 1;
                    }
                }
                return count;
            };

            // Update the header with current count
            const updateHeader = () => {
                const checkedCount = getCheckedCount();
                if (selectHeader) {
                    selectHeader.innerHTML = `${checkedCount} selected`;
                }
            };

            // Initialize header
            updateHeader();

            // Add click listeners to checkboxes
            for (const checkBox of checkBoxes) {
                this.$listen(checkBox, 'click', (e) => {
                    updateHeader();
                });
            }
        }
    }

    private handleApplyButtons() {
        const form = this.querySelector('form') as HTMLFormElement;
        if (!form) {return;}

        // Get all apply buttons
        const applyButtons = this.querySelectorAll(
            'input[type="submit"][value="Apply"]'
        ) as NodeListOf<HTMLInputElement>;

        for (const button of applyButtons) {
            this.$listen(button, 'click', (e) => {
                e.preventDefault();

                // Find the filter section this button belongs to
                const filterSection = button.closest('details') as HTMLDetailsElement;
                if (!filterSection) {return;}

                // Get the current URL
                const currentUrl = new URL(window.location.href);
                const params = new URLSearchParams(currentUrl.search);

                // Keep existing sort_by and search terms
                const sortBy = params.get('sort_by');
                const searchTerms = params.get('q');

                // Get only the inputs from this specific filter section
                const sectionCheckboxes = filterSection.querySelectorAll(
                    'input[type="checkbox"]'
                ) as NodeListOf<HTMLInputElement>;
                const priceInputs = filterSection.querySelectorAll(
                    'input[type="number"]'
                ) as NodeListOf<HTMLInputElement>;

                // Build new URL preserving existing filters from other sections
                const newUrl = new URL(currentUrl.origin + currentUrl.pathname);
                const newParams = new URLSearchParams();

                // Add sort_by if it exists
                if (sortBy) {
                    newParams.set('sort_by', sortBy);
                }

                // Add search terms if it exists
                if (searchTerms) {
                    newParams.set('q', searchTerms);
                }

                // Preserve existing filters from URL that belong to other sections
                const currentSectionParams = this.getCurrentSectionParams(filterSection);
                for (const [key, value] of params.entries()) {
                    // Skip sort_by and search terms as we already handled them
                    if (key === 'sort_by' || key === 'q') {continue;}

                    // Skip the current section's filter parameters - we'll replace them
                    if (currentSectionParams.includes(key)) {continue;}

                    // Add all other existing filter parameters
                    newParams.append(key, value);
                }

                // Add filters from the current section being applied
                for (const checkbox of sectionCheckboxes) {
                    if (checkbox.checked) {
                        newParams.append(checkbox.name, checkbox.value);
                    }
                }

                // Add price filters from current section
                for (const priceInput of priceInputs) {
                    if (priceInput.value) {
                        newParams.set(priceInput.name, priceInput.value);
                    }
                }

                // Navigate to the new URL
                newUrl.search = newParams.toString();
                window.location.href = newUrl.toString();
            });
        }
    }

    private getCurrentSectionParams(filterSection: HTMLDetailsElement): string[] {
        // Get all input names from this section to identify which URL parameters belong to it
        const inputs = filterSection.querySelectorAll(
            'input[type="checkbox"], input[type="number"]'
        ) as NodeListOf<HTMLInputElement>;
        const paramNames: string[] = [];

        for (const input of inputs) {
            if (input.name) {
                paramNames.push(input.name);
            }
        }

        return paramNames;
    }

    private handleClickOutside() {
        // Add click listener to document to handle clicks outside dropdowns
        document.addEventListener('click', (event) => {
            const target = event.target as Element;

            if (!target) {
                return;
            }

            // Check if the click is inside any dropdown content area
            let clickedInsideAnyDropdownContent = false;

            for (const detail of this.details) {
                const dropdownContent = detail.querySelector('.collection-filters__group-display');
                const summary = detail.querySelector('summary');

                // Don't close if clicking on the summary (that opens/closes the dropdown)
                if (summary && summary.contains(target)) {
                    return;
                }

                // Check if clicked inside the dropdown content area
                if (dropdownContent && dropdownContent.contains(target)) {
                    clickedInsideAnyDropdownContent = true;
                    break;
                }
            }

            // If clicked outside all dropdown content areas, close any open ones
            if (!clickedInsideAnyDropdownContent) {
                for (const detail of this.details) {
                    if (detail.hasAttribute('open')) {
                        detail.removeAttribute('open');
                    }
                }
            }
        });

        // Also close dropdowns when pressing Escape key
        document.addEventListener('keydown', (event) => {
            if (event.key === 'Escape') {
                for (const detail of this.details) {
                    if (detail.hasAttribute('open')) {
                        detail.removeAttribute('open');
                    }
                }
            }
        });
    }


    private handlePlantTypeFilters() {
        // Handle Plant Type filter checkboxes - auto-submit on change
        const plantTypeCheckboxes = this.querySelectorAll(
            '.collection-filters__group-display__list--plant-type input[type="checkbox"]'
        ) as NodeListOf<HTMLInputElement>;
        const form = this.querySelector('.collection-filters__form') as HTMLFormElement;

        if (plantTypeCheckboxes.length > 0 && form) {
            for (const checkbox of plantTypeCheckboxes) {
                this.$listen(checkbox, 'change', () => {
                    // Auto-submit the form when Plant Type filter changes
                    form.submit();
                });
            }
        }
    }
}
